import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { CopyStrokeIcon } from "../../assets/icons";
import { REQUEST_ROUTES, TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { useRequestStore } from "../../store/useRequestStore";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { buildShareText, shareOrCopyMessage } from "../../utils/shareMessage";

/**
 * Terminal screen for the request flow. The link box copies the URL only.
 * Share sends the localized request message.
 */
export const RequestReadyPage = memo(() => {
  const { t, i18n } = useTranslation("request");
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { mintResult, draft } = useRequestStore();
  const navigate = useNavigate();

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    dropzoneColors,
    backgroundIcon,
  } = colorConfig;

  const shareLink = mintResult?.url ?? "";

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t("share.manualPrompt"), shareLink);
    }
  };

  const handleShare = async () => {
    if (!shareLink) return;
    const message = buildShareText(t, {
      firstName: session.data?.userData?.name?.firstName,
      amount: draft?.amount,
      currency: draft?.destinationCurrency,
      url: shareLink,
      language: i18n.language,
    });
    await shareOrCopyMessage(message, {
      title: t("share.shareTitle"),
      fallbackPrompt: t("share.manualPrompt"),
    });
  };

  return (
    <AuraLayout colorConfig={colorConfig} className="share-ready-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <RoundedCheckIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
          <PageTitle
            highlight={t("share.title")}
            highlightColor={TITLE_COLOR}
            secudnary={t("share.subtitle")}
            secudnaryColor={TITLE_COLOR}
          />
          <button
            type="button"
            className={`request-link-box ${copied ? "request-link-box--copied" : ""}`.trim()}
            onClick={handleCopy}
            disabled={!shareLink}
            aria-label={copied ? t("share.copied") : t("share.copyLink")}
          >
            <BodyCopy
              textColor={bodyCopy}
              className="request-link-value"
              aria-live="polite"
            >
              {copied ? t("share.copied") : shareLink || "—"}
            </BodyCopy>
            <span className="request-link-copy" aria-hidden="true">
              <CopyStrokeIcon size={20} colors={[bodyCopy, bodyCopy]} />
            </span>
          </button>
        </section>
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section share-ready-actions">
          <Button
            type="button"
            size="large"
            variant="outline"
            background={backgroundBtn}
            color={TITLE_COLOR}
            style={{ backgroundColor: background }}
            onClick={() => navigate(REQUEST_ROUTES.HOME)}
          >
            {t("share.home")}
          </Button>
          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={handleShare}
          >
            {t("share.share")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
